import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

import addIcon from 'assets/images/icons/plus-circle.svg';
import deleteRedIcon from 'assets/images/icons/delete-red.svg';
import editIcon from 'assets/images/icons/edit.svg';
import { RENEWAL, RENEWAL_SUMMARY } from 'constants/routeConstants';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import useReferrerParam from 'hooks/useReferrerParam';
import { setAmendments } from 'actions/expiryRenewalActions';
import Button from 'shared/components/Button/Button';
import { Member, MemberAddAmendment, PaidUpgrade, Vehicle, VehicleAddAmendment, VehicleModifyAmendment } from 'types/expiryRenewal';
import { Store } from 'types/store';
import Footer from 'views/MyRac/ExpiryRenewal/components/atoms/Footer';
import RenewalPrice from 'views/MyRac/ExpiryRenewal/components/atoms/RenewalPrice';

import AddOnOptions from './components/molecules/AddOnOptions';
import MemberModal from './components/molecules/MemberModal/MemberModal';
import VehicleModal from './components/molecules/VehicleModal/VehicleModal';
import Banner from './components/molecules/Banner';
import Collapse from './components/molecules/Collapse';
import FlexToggle from './components/molecules/FlexToggle';
import ViewBasket from './components/molecules/ViewBasket';
import WhatYoureCoveredFor from './components/molecules/WhatYoureCoveredFor';
import Stepper from './components/organisms/Stepper';
import useAmendmentPricing from './hooks/useAmendmentPricing';
import { applyMemberAmendment, isMemberAdd, isVehicleAdd, isVehicleModify, toMember } from './utils/amendmentUtils';

const RenewalCustomise: React.FC = () => {
  const { pageModel, amendments } = useSelector((state: Store) => state?.expiryRenewal);

  const [vehicleBeingEdited, setVehicleBeingEdited] = useState<Vehicle | null>(null);
  const [memberBeingEdited, setMemberBeingEdited] = useState<Member | null>(null);
  const [isAddVehicleOpen, setIsAddVehicleOpen] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);

  // Not destructured: "member" would shadow the mapped member in the members list.
  const pricing = useAmendmentPricing();

  // The sub copy only makes sense when there is in fact something in there to amend.
  const hasRemovableAddons = pageModel?.addonsContent?.some((item) => item?.isRemovable);

  const isAppView: boolean = useReferrerParam();

  /**
   * App view follows the mobile design: each section is its own card on the grey page, spaced 24px
   * apart with 16px of padding and 8px between rows. Web keeps its existing flat layout, where the
   * outlet already sits on white and a card would be invisible against it.
   */
  const sectionSpacing = clsx('fs-6', isAppView ? 'px-3 pt-4' : 'p-3');
  const sectionCard = clsx(isAppView && 'card border-0 rounded p-3');
  const rowSpacing = isAppView ? 'mt-2' : 'mt-3';
  // The collapses are already cards of their own, so they only need the outer gutter.
  const collapseSpacing = clsx('fs-6', isAppView ? 'px-3 pt-4' : 'p-1');

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const members = pageModel?.members ?? [];

  const editedVehiclesList = useMemo(() => (pageModel?.vehicles ?? []).map((vehicle) => {
    const amendment = amendments.find((a): a is VehicleModifyAmendment => isVehicleModify(a) && a.vehicleAmend.oldRegistration === vehicle.Registration);
    if (!amendment) return vehicle;
    return {
      ...vehicle,
      Registration: amendment.vehicleAmend.newRegistration,
      Make: amendment.vehicleAmend.make,
      Model: amendment.vehicleAmend.model,
      YearOfManufacture: String(amendment.vehicleAmend.yearOfManufacture),
    };
  }), [pageModel?.vehicles, amendments]);

  const addedVehiclesList = useMemo(() => amendments
    .filter((a): a is VehicleAddAmendment => isVehicleAdd(a))
    .map((a) => ({
      Registration: a.vehicleAdd.registration,
      Make: a.vehicleAdd.make,
      Model: a.vehicleAdd.model,
      YearOfManufacture: String(a.vehicleAdd.yearOfManufacture),
    })), [amendments]);

  const addedMemberAmendments = useMemo(() => amendments.filter(isMemberAdd), [amendments]);

  const editedMembersList = useMemo(
    () => (pageModel?.members ?? []).map((member) => applyMemberAmendment(member, amendments)),
    [pageModel?.members, amendments],
  );

  const handleDeleteVehicle = (registration: string) => {
    dispatch(setAmendments(
      amendments.filter((a) => !(isVehicleAdd(a) && a.vehicleAdd.registration === registration)),
    ));
  };

  const handleDeleteMember = (amendmentToRemove: MemberAddAmendment) => {
    dispatch(setAmendments(amendments.filter((a) => a !== amendmentToRemove)));
  };

  const { cosmosTrackEvent } = useCosmosTrackEvent('ExpiryRenewals');

  const trackUpgradesDisplayed = (paidUpgrades: PaidUpgrade[] | undefined): void => {
    paidUpgrades?.forEach((upgrade) => {
      const productCode = upgrade?.productCode ?? '';
      if (productCode === 'AtHomeRenewal') {
        cosmosTrackEvent('upgrade_displayed_athome');
      } else if (productCode === 'OnwardTravelRenewal') {
        cosmosTrackEvent('upgrade_displayed_onwardtravel');
      }
    });
  };

  useEffect(() => {
    if (!pageModel) {
      navigate(RENEWAL);
    } else {
      // Send monthly active user data to cosmos db
      cosmosTrackEvent('renewal_journey_customise');
      trackUpgradesDisplayed(pageModel?.paidUpgrades);
    }
  }, [pageModel]);

  return (
    <>
      {(pageModel?.discountModel?.isDiscountedPrice || pageModel?.isEligibleForOneMonthFreeDiscount) && (
        <Banner />
      )}
      <Stepper step={2} />
      {pageModel?.vehicles && pageModel?.vehicles?.length > 0 && (
        <div className={sectionSpacing}>
          <div className={sectionCard}>
            <h5>Your vehicles</h5>
            {pageModel?.vehicles.map((originalVehicle, index) => {
              const displayed = editedVehiclesList[index] ?? originalVehicle;
              return (
                <div className={clsx('card border rounded px-3 py-2 d-flex flex-row justify-content-between align-items-center', rowSpacing)} key={originalVehicle.Registration}>
                  <div className="w-75">
                    <div className="d-block my-2">
                      <span className="regPlate">{displayed.Registration}</span>
                      <span className="mx-1" />
                    </div>
                    <span>{displayed.Make} {displayed.Model}</span>
                  </div>
                  {pageModel?.isVehicleEditAllowed && (
                    <button
                      type="button"
                      className="btn border-0 bg-light rounded-3 p-2 d-flex align-items-center justify-content-center"
                      aria-label={`Edit ${displayed.Registration}`}
                      onClick={() => setVehicleBeingEdited(originalVehicle)}
                    >
                      <img src={editIcon} alt="Edit" width="20" height="20" />
                    </button>
                  )}
                </div>
              );
            })}
            {addedVehiclesList.map((vehicle) => (
              <div className={clsx('card border rounded px-3 py-2 d-flex flex-row justify-content-between align-items-center', rowSpacing)} key={vehicle.Registration}>
                <div className="w-75">
                  <div className="d-block my-2">
                    <span className="regPlate">{vehicle.Registration}</span>
                    <span className="mx-1" />
                  </div>
                  <span>{vehicle.Make} {vehicle.Model}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {pageModel?.isVehicleEditAllowed && (
                    <button
                      type="button"
                      className="btn border-0 bg-light rounded-3 p-2 d-flex align-items-center justify-content-center"
                      aria-label={`Edit ${vehicle.Registration}`}
                      onClick={() => setVehicleBeingEdited(vehicle)}
                    >
                      <img src={editIcon} alt="Edit" width="20" height="20" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn border-0 bg-danger bg-opacity-10 rounded-3 p-2 d-flex align-items-center justify-content-center"
                    aria-label={`Remove ${vehicle.Registration}`}
                    onClick={() => handleDeleteVehicle(vehicle.Registration)}
                  >
                    <img src={deleteRedIcon} alt="" width="18" height="18" />
                  </button>
                </div>
              </div>
            ))}
            {pricing.vehicle.isAddAllowed && (
              <button
                type="button"
                className={clsx('card border rounded px-3 py-2 d-flex flex-row justify-content-between align-items-center w-100 text-start', rowSpacing)}
                style={{ background: 'none', cursor: 'pointer' }}
                onClick={() => setIsAddVehicleOpen(true)}
              >
                <div className="d-flex align-items-center">
                  <img src={addIcon} alt="" width="24" height="24" className="me-2" />
                  <span className="fw-bold">Add vehicle</span>
                </div>
                <div>
                  <RenewalPrice size="lg" price={pricing.vehicle.nowPrice} period={pricing.periodLabel} wasPrice={pricing.vehicle.wasPrice} />
                </div>
              </button>
            )}
          </div>
        </div>
      )}
      {((members.length ?? 0) > 0 || addedMemberAmendments.length > 0 || pricing.member.isAddAllowed) && (
        <div className={sectionSpacing}>
          <div className={sectionCard}>
            <h5>Your members</h5>
            {members.map((originalMember, index) => {
              // Keyed on the member as the quote returned them, so a staged edit does not
              // remount the card. The lead member never carries an amendment.
              const key = `${originalMember.firstName}-${originalMember.surname}-${originalMember.dateOfBirth}`;
              const displayed = editedMembersList[index] ?? originalMember;
              return originalMember.leadMember ? (
                <div
                  className={clsx('rounded-1 px-3 py-3', rowSpacing)}
                  style={{ background: '#dbdbdb' }}
                  key={key}
                >
                  <div className="text-muted small">Lead member</div>
                  <div className="d-flex flex-row justify-content-between align-items-center">
                    <span className="fw-bold text-truncate me-2" style={{ minWidth: 0 }}>{displayed.title} {displayed.firstName} {displayed.surname}</span>
                    {displayed.hasDateOfBirth && (
                      <span className="text-muted flex-shrink-0">{new Date(displayed.dateOfBirth).toLocaleDateString('en-GB')}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className={clsx('card border rounded px-3 py-3 d-flex flex-row justify-content-between align-items-center', rowSpacing)} key={key}>
                  <div className="me-2" style={{ minWidth: 0 }}>
                    <div className="fw-bold text-truncate">{displayed.title} {displayed.firstName} {displayed.surname}</div>
                    {displayed.hasDateOfBirth && (
                      <span className="text-muted">{new Date(displayed.dateOfBirth).toLocaleDateString('en-GB')}</span>
                    )}
                  </div>
                  {pageModel?.isMemberEditAllowed && (
                    <button
                      type="button"
                      className="btn border-0 bg-light rounded-3 p-2 d-flex align-items-center justify-content-center flex-shrink-0"
                      aria-label={`Edit ${displayed.firstName} ${displayed.surname}`}
                      onClick={() => setMemberBeingEdited(originalMember)}
                    >
                      <img src={editIcon} alt="Edit" width="20" height="20" />
                    </button>
                  )}
                </div>
              );
            })}
            {addedMemberAmendments.map((amendment) => {
              const member = toMember(amendment);
              return (
                <div className={clsx('card border rounded px-3 py-3 d-flex flex-row justify-content-between align-items-center', rowSpacing)} key={`added-${member.firstName}-${member.surname}-${member.dateOfBirth}`}>
                  <div className="me-2" style={{ minWidth: 0 }}>
                    <div className="fw-bold text-truncate">{member.title} {member.firstName} {member.surname}</div>
                    <span className="text-muted">{new Date(member.dateOfBirth).toLocaleDateString('en-GB')}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    {/*
                      Not gated on isMemberEditAllowed: that flag governs the members the quote
                      came back with. A member added during this journey is only a staged
                      amendment, so it stays editable (and removable) either way.
                    */}
                    <button
                      type="button"
                      className="btn border-0 bg-light rounded-3 p-2 d-flex align-items-center justify-content-center"
                      aria-label={`Edit ${member.firstName} ${member.surname}`}
                      onClick={() => setMemberBeingEdited(member)}
                    >
                      <img src={editIcon} alt="Edit" width="20" height="20" />
                    </button>
                    <button
                      type="button"
                      className="btn border-0 bg-danger bg-opacity-10 rounded-3 p-2 d-flex align-items-center justify-content-center"
                      aria-label={`Remove ${member.firstName} ${member.surname}`}
                      onClick={() => handleDeleteMember(amendment)}
                    >
                      <img src={deleteRedIcon} alt="" width="18" height="18" />
                    </button>
                  </div>
                </div>
              );
            })}
            {pricing.member.isAddAllowed && (
              <button
                type="button"
                className={clsx('card border rounded px-3 py-2 d-flex flex-row justify-content-between align-items-center w-100 text-start', rowSpacing)}
                style={{ background: 'none', cursor: 'pointer' }}
                onClick={() => setIsAddMemberOpen(true)}
              >
                <div className="d-flex align-items-center">
                  <img src={addIcon} alt="" width="24" height="24" className="me-2" />
                  <span className="fw-bold">Add member</span>
                </div>
                <div>
                  <RenewalPrice size="lg" price={pricing.member.nowPrice} period={pricing.periodLabel} wasPrice={pricing.member.wasPrice} />
                </div>
              </button>
            )}
          </div>
        </div>
      )}
      <div className={collapseSpacing}>
        {pageModel?.coverTypeTitle && (
          <Collapse
            title="What you're covered for"
            description={hasRemovableAddons ? 'Amend your cover' : undefined}
            defaultOpen={false}
          >
            <WhatYoureCoveredFor />
          </Collapse>
        )}
      </div>
      <div className={collapseSpacing}>
        {pageModel?.paidUpgrades && pageModel?.paidUpgrades?.length > 0 && (
          <Collapse title="What you're not covered for" description="Select to add to cover">
            <AddOnOptions additionalUnits={pricing.vehicle.addedCount + pricing.member.addedCount} />
          </Collapse>
        )}
      </div>
      <div className="card border fluid rounded-0 mt-2 p-3">
        <FlexToggle />
        <ViewBasket />
        <Button
          onClickHandler={() => navigate(RENEWAL_SUMMARY)}
          buttonText="Continue"
        />
      </div>
      <Footer />
      {vehicleBeingEdited && (
        <VehicleModal
          mode="edit"
          vehicle={vehicleBeingEdited}
          onClose={() => setVehicleBeingEdited(null)}
        />
      )}
      {isAddVehicleOpen && (
        <VehicleModal mode="add" onClose={() => setIsAddVehicleOpen(false)} />
      )}
      {memberBeingEdited && (
        <MemberModal
          mode="edit"
          member={memberBeingEdited}
          onClose={() => setMemberBeingEdited(null)}
        />
      )}
      {isAddMemberOpen && (
        <MemberModal mode="add" onClose={() => setIsAddMemberOpen(false)} />
      )}
    </>
  );
};

export default RenewalCustomise;
